import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import z from 'zod';

import { mutateUserUpdate, type UserType } from '@/api/user';
import NotificationSubscriptionsTable from '@/app/profile/NotificationSubscriptionsTable';
import ProfileHeader from '@/app/profile/ProfileHeader';
import UserDetailInfoFields from '@/app/users/[user]/UserDetailInfoFields';
import { useAppForm } from '@/components/Form';
import { Panel } from '@/components/panels';
import { FEATURE_FLAG } from '@/constants/featureFlags';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import { PermissionGroup } from '@/features/PermissionGroup';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useNotification } from '@/providers/Notification';

type Props = {
  user: UserType;
  isC99Superuser?: boolean;
  onSubmitSuccess: (newUser: UserType) => void;
};

const ProfileContent = ({ user, isC99Superuser, onSubmitSuccess }: Props) => {
  const { pushNotification, removeNotification } = useNotification();
  const isAIDigestNextEnabled = useFeatureFlag(FEATURE_FLAG.AI_DIGEST_NEXT) === true;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const formSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t`Name is required`),
    email: z.string(),
    role: z.enum(['user', 'admin'] as const),
    digestDelivery: z.object({
      repeatsWeeks: z.number(),
    }),
  });

  const form = useAppForm({
    defaultValues: user as Omit<UserType, 'id'>,
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          setSaving(true);

          const { name, role, digestDelivery } = value;
          await mutateUserUpdate({ id: user.id, name, role, digestDelivery });

          onSubmitSuccess({
            ...user,
            ...value,
          });

          pushNotification({ type: 'success', message: t`Your profile has been updated.` });
          removeNotification({ type: 'error' });
        } catch (error) {
          pushNotification({
            type: 'error',
            message: getStytchAPIErrorMessage(error),
          });

          return {
            name: [
              {
                message: t`An error occurred while trying to save your name. Please refresh the page and try again.`,
              },
            ],
          };
        } finally {
          setSaving(false);
          setIsEditing(false);
        }
      },
    },
  });

  const handleEditChange = (isEditing: boolean) => {
    if (!isEditing) {
      form.reset(user, { keepDefaultValues: true });
    }
    setIsEditing(isEditing);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex w-full flex-col gap-6">
          <ProfileHeader
            isC99Superuser={isC99Superuser ?? false}
            isEditing={isEditing}
            isSaving={isSaving}
            onEditChange={handleEditChange}
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-24">
            <div className="lg:col-span-24 xl:col-span-9 2xl:col-span-7">
              <Panel title={<Trans>User Details</Trans>} style={{ height: '100%' }}>
                <UserDetailInfoFields
                  userId={user.id}
                  isDigestDeliveryEditable={!isAIDigestNextEnabled}
                  isEmailEditable={false}
                  isEditing={isEditing}
                  isSaving={isSaving}
                />
              </Panel>
            </div>
            <div className="lg:col-span-24 xl:col-span-15 2xl:col-span-17">
              <Panel title={<Trans>Permission Setting</Trans>} style={{ height: '100%' }}>
                <form.AppField name="role">
                  {(field) => (
                    <field.Field>
                      <PermissionGroup
                        isEditing={false}
                        isSaving={isSaving}
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                      />
                      <field.Error />
                    </field.Field>
                  )}
                </form.AppField>
              </Panel>
            </div>
            <div className="lg:col-span-24">
              <NotificationSubscriptionsTable />
            </div>
          </div>
        </div>
      </form.AppForm>
    </form>
  );
};

export default ProfileContent;
